import { Controller } from "@hotwired/stimulus"

// Shows the picked image right away, then sends it on its own so the form itself stays tiny.
// If that fails for any reason, the file input keeps its name and the normal form upload still runs.
export default class extends Controller {
  static targets = ["input", "image", "placeholder", "field", "status"]
  static values = { url: String, name: String }

  show() {
    const file = this.inputTarget.files?.[0]
    if (!file) return

    if (file.size === 0) {
      this.fail(this.element.dataset.emptyMessage)
      return
    }

    if (file.type.startsWith("image/")) {
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = URL.createObjectURL(file)
      this.imageTarget.src = this.objectUrl
      this.imageTarget.hidden = false
      this.placeholderTargets.forEach((element) => { element.hidden = true })
    }

    if (this.hasFieldTarget && this.hasUrlValue) this.send(file)
  }

  // Straight binary POST: no multipart, no form fields, nothing for a browser to drop.
  send(file) {
    const request = new XMLHttpRequest()
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    this.setStatus(this.element.dataset.sendingMessage, 0)
    this.fieldTarget.removeAttribute("name")

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("filename", file.name)
    request.open("POST", url.toString(), true)
    request.setRequestHeader("Content-Type", file.type || "application/octet-stream")
    if (token) request.setRequestHeader("X-CSRF-Token", token)

    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) this.setStatus(this.element.dataset.sendingMessage, Math.round((event.loaded / event.total) * 100))
    })

    request.addEventListener("load", () => {
      if (request.status < 200 || request.status >= 300) return this.fallBackToForm()

      try {
        const { signed_id: signedId } = JSON.parse(request.responseText)
        if (!signedId) return this.fallBackToForm()
        this.fieldTarget.value = signedId
        this.fieldTarget.setAttribute("name", this.nameValue)
        this.inputTarget.removeAttribute("name")
        this.setStatus(this.element.dataset.readyMessage)
      } catch {
        this.fallBackToForm()
      }
    })

    request.addEventListener("error", () => this.fallBackToForm())
    request.addEventListener("abort", () => this.fallBackToForm())
    request.send(file)
  }

  // Let the ordinary multipart form carry the file after all.
  fallBackToForm() {
    this.fieldTarget.value = ""
    this.fieldTarget.removeAttribute("name")
    this.inputTarget.setAttribute("name", this.nameValue)
    this.setStatus("")
  }

  fail(message) {
    this.inputTarget.value = ""
    this.setStatus(message)
  }

  setStatus(message, percent) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = percent > 0 && percent < 100 ? `${message} ${percent}%` : (message || "")
  }

  disconnect() {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
  }
}
